import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useUser } from "../contexts/UserContext";
import {
  latestConsentByPolicy, consentStateFor, needsPrompt, isConsentTarget,
} from "../lib/policyConsentRules";
import { SHORTFORM_POLICY_KEY } from "../lib/policyDocuments";
import PolicyConsentModal from "./PolicyConsentModal";

// ============================================================================
// 숏폼 정책 동의 게이트 — UserLayout 에 마운트되어 화면 진입마다 노출 여부를 판정
//
// 노출 조건 (전부 만족 시):
//  * policy(shortform_revenue).is_active = true  ← 배포 스위치 (기본 false 시드)
//  * 크리에이터 (role 부분 일치, Admin/Platform 겸직은 제외) — isConsentTarget
//  * 현재 버전에 대한 동의가 없음 (거절·철회·유보는 다음 화면 진입 시 재노출 — §6-1)
//  * /policies 경로가 아님 (전문을 읽는 화면을 팝업이 막지 않도록)
//
// 2026-08-31 변경 — 채널 조건 제거 (대표 확정):
//   이전에는 shortform_eligibility='supported' 인 **채널**을 가진 사람만 대상이었다.
//   채널 등록은 크리에이터의 자발적 행위라, 네이버·카카오를 실제로 운영해도 등록하지
//   않으면 대상에서 통째로 빠졌다 — 크리에이터 157명 중 127명이 그랬다.
//   이제 크리에이터 전원에게 띄우고, 지원/자력 판정(user.shortform_eligibility)은
//   "정책이 실제로 적용되는가"(정산)를 정하는 별도 값으로 둔다.
//
// 테이블이 아직 없거나 조회가 실패하면 조용히 노출하지 않는다 — 게이트가
// 앱을 깨뜨리면 안 된다.
// ============================================================================

const PolicyConsentGate = () => {
  const { userInfo } = useUser();
  const location = useLocation();
  const [policy, setPolicy] = useState(null);
  const [open, setOpen] = useState(false);
  const [deferredPath, setDeferredPath] = useState(null);

  const email = userInfo?.email;
  const role = userInfo?.role;

  const evaluate = useCallback(async () => {
    try {
      if (!email || !isConsentTarget(role)) return setOpen(false);
      if (location.pathname.startsWith("/policies")) return setOpen(false);
      if (deferredPath === location.pathname) return; // '나중에' — 같은 화면에서는 다시 열지 않는다

      const { data: policies, error: pErr } = await supabase
        .from("policy")
        .select("key, title, version, is_required, is_active, grace_until, effective_from")
        .eq("key", SHORTFORM_POLICY_KEY)
        .eq("is_active", true)
        .limit(1);
      if (pErr || !policies || policies.length === 0) return setOpen(false);
      const target = policies[0];

      const { data: consents, error: uErr } = await supabase
        .from("user_policy_consent")
        .select("policy_key, version, agreed, method, created_at")
        .eq("user_email", email)
        .eq("policy_key", SHORTFORM_POLICY_KEY);
      if (uErr) return setOpen(false);

      const latest = latestConsentByPolicy(consents)[SHORTFORM_POLICY_KEY];
      const state = consentStateFor(target, latest);
      if (needsPrompt(target, state)) {
        setPolicy(target);
        setOpen(true);
      } else {
        setOpen(false);
      }
    } catch (e) {
      setOpen(false);
    }
  }, [email, role, location.pathname, deferredPath]);

  useEffect(() => {
    evaluate();
  }, [evaluate]);

  if (!open || !policy) return null;

  return (
    <PolicyConsentModal
      isOpen={open}
      policy={policy}
      userEmail={email}
      onDecision={() => setOpen(false)}
      onDefer={() => {
        setDeferredPath(location.pathname);
        setOpen(false);
      }}
    />
  );
};

export default PolicyConsentGate;
